import React from 'react';
import { MapPin, Award, Shield, CheckCircle, Phone } from 'lucide-react';

const GoaTourismPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Hero */}
      <div className="relative h-96 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1920&q=80"
          alt="Goa Tourism"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/56 to-transparent"></div>
        <div className="absolute inset-0 flex items-center px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <h1 className="text-6xl md:text-8xl font-black text-white uppercase tracking-tight mb-4">
              Goa <span className="text-yellow-400">Tourism</span>
            </h1>
            <p className="text-xl text-gray-300">Endorsing Safe Adventure Tourism in Goa</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-20 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {/* Introduction */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <MapPin className="text-yellow-400 flex-shrink-0 mt-1" size={40} />
                <div>
                  <h2 className="text-3xl font-bold text-white mb-4">Goa Tourism Endorses Bungee Jumping</h2>
                </div>
              </div>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Goa is one of India's most loved tourist destinations, famous for its beaches, nightlife, and thrilling adventure activities. Among these, bungee jumping in Goa has become a major attraction for thrill-seekers and adventure enthusiasts from across the country.
                </p>
                <p>
                  The <strong className="text-white">Goa Tourism Development Corporation (GTDC)</strong>, under the Government of Goa, actively promotes adventure tourism in Goa and encourages safe and regulated bungee jumping experiences for visitors. While the GTDC does not directly regulate bungee jumping or other adventure activities, it provides valuable guidance and information to tourists looking for trusted and licensed operators.
                </p>
              </div>
            </section>

            {/* Operators in Goa */}
            <section className="bg-white/5 border border-white/10 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-white mb-4">Certified Operators</h3>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Currently, two private operators offer professional and certified bungee jumping experiences in Goa, ensuring that safety standards and international guidelines are followed. This makes Goa one of the top destinations for bungee jumping in India, attracting thousands of adrenaline lovers every year.
                </p>
              </div>
            </section>

            {/* Recommendations */}
            <section className="bg-yellow-400/10 border border-yellow-400/20 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Shield className="text-yellow-400 flex-shrink-0 mt-1" size={32} />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Safety Recommendations</h3>
                </div>
              </div>
              <p className="text-gray-300 mb-4">
                If you are planning to experience bungee jumping in Goa, it's highly recommended to choose a reputable and certified operator who follows proper safety measures:
              </p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>Uses quality equipment</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>Employs trained instructors</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>Follows international safety standards</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-yellow-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                  <span>Government-registered and recognized</span>
                </li>
              </ul>
              <p className="text-gray-300 mt-4">
                You can also consult the GTDC for verified information and recommendations on adventure sports in Goa.
              </p>
            </section>

            {/* Featured Image */}
            <section className="rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1200&q=80"
                alt="Goa Tourism"
                className="w-full h-64 object-cover"
              />
            </section>

            {/* Indya Bungy Recognition */}
            <section className="bg-gradient-to-br from-green-500/10 to-green-900/10 border border-green-500/20 rounded-xl p-8">
              <div className="flex items-start space-x-4 mb-6">
                <Award className="text-green-400 flex-shrink-0 mt-1" size={32} />
                <h3 className="text-2xl font-bold text-white">Goa Bungy by Indya Bungy</h3>
              </div>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Goa Bungy, operated by Indya Bungy, is a trusted and <strong className="text-white">government-registered bungee jumping operator</strong> offering a safe and thrilling bungee jumping experience in Goa. We are recognized by:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                    <span><strong className="text-white">Ministry of Tourism (MOT)</strong></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                    <span><strong className="text-white">Government of Goa</strong></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                    <span><strong className="text-white">Goa Tourism</strong></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="text-green-400 mr-2 flex-shrink-0 mt-0.5" size={20} />
                    <span>Proud allied member of <strong className="text-white">ATOAI</strong></span>
                  </li>
                </ul>
                <p className="mt-4">
                  At Indya Bungy, we follow strict safety standards, certified procedures, and international guidelines such as SANZ (Standards of Australia and New Zealand) to ensure every jump is completely safe. Our team of expert jump masters guarantees that your experience is both heart-pounding and secure — making us one of India's best bungee jumping destinations.
                </p>
              </div>
            </section>

            {/* CTA */}
            <section className="text-center">
              <a
                href="tel:+918506865393"
                className="inline-flex items-center space-x-3 bg-yellow-400 hover:bg-yellow-300 text-black px-8 py-4 rounded-full font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg shadow-yellow-400/50"
              >
                <Phone size={24} />
                <span>Call Us: +91 8506865393</span>
              </a>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoaTourismPage;